import React, { FormEventHandler, ReactNode } from "react"
import { Translates } from "@/services/repository/i18n"
import { FieldValues, Path, SubmitHandler, UseFormReturn } from 'react-hook-form'
import { Services } from "@/services/services";

export interface FormDataBase {
  _general?: string;
  _details?: string;
}

export interface ServerError {
  message: string;
}

export type ServerErrors<T> = {
  [K in keyof T]: ServerError;
}

export type onSubmit<T> = (
  data: T
) => Promise<void>

export interface BodyParams<T extends FormDataBase> {
  services: Services
  form: UseFormReturn<T>
  translates: Translates
  formName: string
  readOnly: boolean
  lock?: boolean
}

export function addServerErrors<T extends FieldValues>(form: UseFormReturn<T>, translates: Translates, errors: ServerErrors<T>) {
  for (let i in errors) {
    const key = i as any
    form.setError(key, {
      type: 'server',
      message: translates?.for(errors[key]!.message),
    })
    form.setFocus(key)
  }
}

export function ErrorBoxComponent<T extends FieldValues>(form: UseFormReturn<T>, translates: Translates, field: keyof T) {
  const error = form.formState.errors[field]
  if (form.formState.errors[field]) {
    return <p className="errorMessage">{(error as ServerError).message}</p>;
  }
  return null;
}

export interface FieldProps<T extends FormDataBase> {
  formName: string
  fieldName: Path<T>
  form: UseFormReturn<T>
  translates: Translates
  services: Services
  readOnly: boolean
  required: boolean
}

export interface FieldWrapProps<P extends object> {
  WrappedComponent: React.ComponentType<P>
}

export function withFieldWrap<T extends FieldValues>(
  WrappedComponent: React.ComponentType<FieldProps<T>>
): React.FC<FieldProps<T>> {
  const EnhancedComponent: React.FC<FieldProps<T>> = (props) => {
    if(!props.formName) {
      throw new Error("formName is required")
    }
    if(!props.fieldName) {
      throw new Error("fieldName is required")
    }
    return (
      <div className="form-field">
        <label htmlFor={props.fieldName}>
          {props.translates.for(`forms.${props.formName}.fields.${props.fieldName}.title`)}:
        </label>
        <WrappedComponent {...props} />
        {ErrorBoxComponent(props.form, props.translates, props.fieldName)}
      </div>
    )
  }
  return EnhancedComponent;
}

export interface FormParams<T extends FieldValues> {
  form: UseFormReturn<T>
  onSubmit: SubmitHandler<T>
  children: ReactNode
  formName: string
  translates: Translates
  className?: string
  lock?:boolean
  hideSubmitBtn?: boolean
}

export const FormWrapper = <T extends FormDataBase>(props: FormParams<T>) => {
  const {
    onSubmit,
    form,
    formName,
    translates,
    children,
    className,
    lock,
    hideSubmitBtn
  } = props
  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const generalError = errors._general ? <p className="errorMessage">{typeof errors._general.message === 'string'
    ? errors._general.message
    : null}</p> : null

  return (
    <form className={className ?? 'main-form'} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      {children}
      { !hideSubmitBtn ? <button className="submit-button" type="submit" disabled={lock}>
        {translates.for(`forms.${formName}.submit`)}
      </button> : null }
      {generalError}
    </form>
  )
}